export type GroupTalkTemplateId = "management" | "project" | "professional" | "custom";

export type GroupTalkMemberId =
  | "mei"
  | "cmo"
  | "cto"
  | "cfo"
  | "coo"
  | "pm"
  | "tax_accountant"
  | "lawyer"
  | "social_insurance_advisor"
  | "customer"
  | "consultant";

export type GroupTalkStanceId =
  | "bold"
  | "cautious"
  | "creative"
  | "realist"
  | "numbers"
  | "user_first"
  | "skeptic"
  | "contrarian";

export type GroupTalkDraftMode = "recommend" | "template" | "custom";
export type GroupTalkDisplayMode = "named" | "virtual";

export type GroupTalkRoleStanceOption = {
  id: GroupTalkStanceId;
  label: string;
  description: string;
};

export type GroupTalkTemplateMemberConfig = {
  memberId: GroupTalkMemberId;
  stanceId: GroupTalkStanceId | null;
};

export type GroupTalkTemplateConfig = {
  id: GroupTalkTemplateId;
  title: string;
  description: string;
  members: GroupTalkTemplateMemberConfig[];
};

export type GroupTalkMemberConfig = {
  id: GroupTalkMemberId;
  label: string;
  roleLabel: string;
  fixed?: boolean;
};

export type GroupTalkParticipant = {
  participantId: string;
  memberId: GroupTalkMemberId;
  stanceId: GroupTalkStanceId | null;
  fixed: boolean;
  availableStances?: GroupTalkRoleStanceOption[];
  displayRole?: string;
  displayName?: string;
  personaKey?: string;
  avatarKey?: string;
  imageSrc?: string;
  imageVariant?: 1 | 2;
  /** LLM 推薦時の選定理由（表示用。任意） */
  reason?: string;
};

export type GroupTalkPreviewMember = {
  participantId: string;
  memberId: GroupTalkMemberId;
  label: string;
  roleLabel: string;
  stanceId: GroupTalkStanceId | null;
  stanceLabel: string | null;
  basicStanceLabel: string | null;
  fixed: boolean;
  personaKey?: string;
  avatarKey?: string;
  imageSrc: string;
};

const GROUP_TALK_IMAGE_BASE_PATH = "/img/group-talk";
const GROUP_TALK_FALLBACK_IMAGE_PATH = "/img/no-image.png";
const GROUP_TALK_MEMBER_IDS: GroupTalkMemberId[] = [
  "mei",
  "cmo",
  "cto",
  "cfo",
  "coo",
  "pm",
  "tax_accountant",
  "lawyer",
  "social_insurance_advisor",
  "customer",
  "consultant",
];
const GROUP_TALK_STANCE_IDS: GroupTalkStanceId[] = [
  "bold",
  "cautious",
  "creative",
  "realist",
  "numbers",
  "user_first",
  "skeptic",
  "contrarian",
];
const GROUP_TALK_LEGACY_MEMBER_ID_MAP: Record<string, GroupTalkMemberId> = {
  tax: "tax_accountant",
  law: "lawyer",
  labor: "social_insurance_advisor",
};
const GROUP_TALK_LEGACY_STANCE_ID_MAP: Record<string, GroupTalkStanceId> = {
  careful: "cautious",
  idea: "creative",
  // 旧 key 名との互換（新 backend の stance_key に合わせて 1 箇所へ集約する）
  strong: "bold",
  realistic: "realist",
  numeric: "numbers",
  skeptical: "skeptic",
};
const GROUP_TALK_RECOMMENDATION_TEMPLATE_ID_MAP: Record<string, GroupTalkTemplateId> = {
  business_growth: "management",
  product_planning: "project",
  legal_finance_hr: "professional",
};
const GROUP_TALK_IMAGE_NAME_BY_MEMBER_ID: Partial<Record<GroupTalkMemberId, string>> = {
  tax_accountant: "tax",
  lawyer: "law",
  social_insurance_advisor: "labor",
};

const GROUP_TALK_MEMBER_DISPLAY: Record<
  GroupTalkMemberId,
  {
    label: string;
    namedLabel: string;
    virtualLabel: string;
    roleLabel: string;
  }
> = {
  mei: {
    label: "メイ",
    namedLabel: "日向 メイ",
    virtualLabel: "日向 メイ",
    roleLabel: "相談を整理して進行する",
  },
  cmo: {
    label: "CMO",
    namedLabel: "山田CMO",
    virtualLabel: "水野CMO",
    roleLabel: "売り方・集客を見る",
  },
  cto: {
    label: "CTO",
    namedLabel: "森CTO",
    virtualLabel: "黒瀬CTO",
    roleLabel: "技術と実現性を見る",
  },
  cfo: {
    label: "CFO",
    namedLabel: "佐藤CFO",
    virtualLabel: "田辺CFO",
    roleLabel: "採算とお金を見る",
  },
  coo: {
    label: "COO",
    namedLabel: "黒田COO",
    virtualLabel: "河野COO",
    roleLabel: "運用と実行を見る",
  },
  pm: {
    label: "PM",
    namedLabel: "早川PM",
    virtualLabel: "宮田PM",
    roleLabel: "使いやすさを見る",
  },
  tax_accountant: {
    label: "税理士",
    namedLabel: "三浦税理士",
    virtualLabel: "岡田税理士",
    roleLabel: "税務とお金を見る",
  },
  lawyer: {
    label: "弁護士",
    namedLabel: "北村弁護士",
    virtualLabel: "三浦弁護士",
    roleLabel: "契約リスクを見る",
  },
  social_insurance_advisor: {
    label: "社労士",
    namedLabel: "青木社労士",
    virtualLabel: "大谷社労士",
    roleLabel: "雇用と労務を見る",
  },
  customer: {
    label: "顧客代弁者",
    namedLabel: "小野寺さん",
    virtualLabel: "片桐さん",
    roleLabel: "顧客の本音を見る",
  },
  consultant: {
    label: "コンサル",
    namedLabel: "高橋コンサル",
    virtualLabel: "藤井コンサル",
    roleLabel: "論点を整理する",
  },
};

export const groupTalkMemberOptions: GroupTalkMemberConfig[] = [
  { id: "mei", label: GROUP_TALK_MEMBER_DISPLAY.mei.label, roleLabel: "ファシリテータ", fixed: true },
  { id: "cmo", label: GROUP_TALK_MEMBER_DISPLAY.cmo.label, roleLabel: GROUP_TALK_MEMBER_DISPLAY.cmo.roleLabel },
  { id: "cto", label: GROUP_TALK_MEMBER_DISPLAY.cto.label, roleLabel: GROUP_TALK_MEMBER_DISPLAY.cto.roleLabel },
  { id: "cfo", label: GROUP_TALK_MEMBER_DISPLAY.cfo.label, roleLabel: GROUP_TALK_MEMBER_DISPLAY.cfo.roleLabel },
  { id: "coo", label: GROUP_TALK_MEMBER_DISPLAY.coo.label, roleLabel: GROUP_TALK_MEMBER_DISPLAY.coo.roleLabel },
  { id: "pm", label: GROUP_TALK_MEMBER_DISPLAY.pm.label, roleLabel: GROUP_TALK_MEMBER_DISPLAY.pm.roleLabel },
  { id: "tax_accountant", label: GROUP_TALK_MEMBER_DISPLAY.tax_accountant.label, roleLabel: GROUP_TALK_MEMBER_DISPLAY.tax_accountant.roleLabel },
  { id: "lawyer", label: GROUP_TALK_MEMBER_DISPLAY.lawyer.label, roleLabel: GROUP_TALK_MEMBER_DISPLAY.lawyer.roleLabel },
  { id: "social_insurance_advisor", label: GROUP_TALK_MEMBER_DISPLAY.social_insurance_advisor.label, roleLabel: GROUP_TALK_MEMBER_DISPLAY.social_insurance_advisor.roleLabel },
  { id: "customer", label: GROUP_TALK_MEMBER_DISPLAY.customer.label, roleLabel: GROUP_TALK_MEMBER_DISPLAY.customer.roleLabel },
  { id: "consultant", label: GROUP_TALK_MEMBER_DISPLAY.consultant.label, roleLabel: GROUP_TALK_MEMBER_DISPLAY.consultant.roleLabel },
];

export const groupTalkStanceOptions: { id: GroupTalkStanceId; label: string }[] = [
  { id: "bold", label: "強気" },
  { id: "cautious", label: "慎重" },
  { id: "creative", label: "発想派" },
  { id: "realist", label: "現実派" },
  { id: "numbers", label: "数字重視" },
  { id: "user_first", label: "ユーザー至上" },
  { id: "skeptic", label: "懐疑派" },
  { id: "contrarian", label: "逆張り" },
];

export const groupTalkRoleStanceOptions: Partial<Record<GroupTalkMemberId, GroupTalkRoleStanceOption[]>> = {
  cmo: [
    { id: "bold", label: "攻めの販売戦略", description: "新規顧客獲得や市場拡大を重視します。" },
    { id: "realist", label: "現実的に売る", description: "今あるリソースで実行しやすい販売策を考えます。" },
    { id: "user_first", label: "顧客目線で見る", description: "顧客に伝わる言葉や訴求を重視します。" },
  ],
  cfo: [
    { id: "numbers", label: "数字で見る", description: "採算、利益率、費用対効果を重視します。" },
    { id: "cautious", label: "慎重に見る", description: "資金繰りやリスクを抑えた判断を重視します。" },
    { id: "bold", label: "成長投資で見る", description: "将来の成長につながる投資判断を重視します。" },
  ],
  cto: [
    { id: "realist", label: "現実的に作る", description: "開発負荷や実現性を重視します。" },
    { id: "creative", label: "新しい技術で広げる", description: "技術による新しい可能性を提案します。" },
    { id: "cautious", label: "リスクを慎重に見る", description: "技術負債、品質、運用リスクを重視します。" },
  ],
  pm: [
    { id: "user_first", label: "ユーザー体験で見る", description: "使いやすさ、分かりやすさ、体験価値を重視します。" },
    { id: "creative", label: "新しい体験を考える", description: "新しい機能や体験の可能性を広げます。" },
    { id: "realist", label: "優先順位を整理する", description: "今やるべきこと、後回しにすることを整理します。" },
  ],
  consultant: [
    { id: "realist", label: "実行しやすく整理", description: "限られたリソースで進めやすい案に整理します。" },
    { id: "creative", label: "論点を広く見る", description: "抜け漏れや新しい可能性を広げます。" },
    { id: "contrarian", label: "別の見方を出す", description: "一般論とは違う視点から考えます。" },
  ],
  customer: [
    { id: "skeptic", label: "買わない理由を見る", description: "顧客が不安に感じる点や離脱理由を考えます。" },
    { id: "bold", label: "買いたい理由を見る", description: "顧客が欲しくなる理由や支払い意欲を考えます。" },
    { id: "user_first", label: "顧客目線で見る", description: "顧客にとって分かりやすい価値を重視します。" },
  ],
  lawyer: [
    { id: "cautious", label: "契約リスクを見る", description: "法務・契約上のリスクを慎重に確認します。" },
    { id: "realist", label: "事業を止めずに考える", description: "リスクを抑えつつ、事業を進める現実案を考えます。" },
    { id: "skeptic", label: "見落としを疑う", description: "契約上の抜け漏れや曖昧さを疑います。" },
  ],
  tax_accountant: [
    { id: "numbers", label: "税務とお金を見る", description: "税務、資金繰り、会計上の影響を見ます。" },
    { id: "cautious", label: "慎重に見る", description: "税務リスクや将来の負担を慎重に考えます。" },
    { id: "realist", label: "現実的に進める", description: "実務上進めやすい対応を考えます。" },
  ],
  social_insurance_advisor: [
    { id: "cautious", label: "労務リスクを見る", description: "雇用、労務、社会保険上のリスクを見ます。" },
    { id: "realist", label: "現実的に進める", description: "実務上進めやすい対応を考えます。" },
    { id: "user_first", label: "働く人の目線で見る", description: "従業員や委託先にとっての納得感を重視します。" },
  ],
};

export const groupTalkTemplates: GroupTalkTemplateConfig[] = [
  {
    id: "management",
    title: "経営・売上の相談",
    description: "売上、価格、集客、ターゲット、事業方針を整理します。",
    members: [
      { memberId: "mei", stanceId: null },
      { memberId: "cmo", stanceId: "bold" },
      { memberId: "cfo", stanceId: "numbers" },
      { memberId: "customer", stanceId: "skeptic" },
      { memberId: "consultant", stanceId: "contrarian" },
    ],
  },
  {
    id: "project",
    title: "商品・企画の相談",
    description: "新機能、商品改善、UX、開発優先度を整理します。",
    members: [
      { memberId: "mei", stanceId: null },
      { memberId: "pm", stanceId: "user_first" },
      { memberId: "cto", stanceId: "realist" },
      { memberId: "customer", stanceId: "skeptic" },
      { memberId: "consultant", stanceId: "creative" },
    ],
  },
  {
    id: "professional",
    title: "契約・お金・人事労務の相談",
    description: "契約、税務、採用面接、雇用条件、労務、リスクを一般的な観点で整理します。",
    members: [
      { memberId: "mei", stanceId: null },
      { memberId: "lawyer", stanceId: "cautious" },
      { memberId: "tax_accountant", stanceId: "numbers" },
      { memberId: "social_insurance_advisor", stanceId: "user_first" },
      { memberId: "consultant", stanceId: "realist" },
    ],
  },
  {
    id: "custom",
    title: "メンバーを自由に選ぶ",
    description: "相談内容に合わせて、参加メンバーを自由に選べます。",
    members: [{ memberId: "mei", stanceId: null }],
  },
];

export const defaultGroupTalkStances: Record<GroupTalkMemberId, GroupTalkStanceId | null> = {
  mei: null,
  cmo: "bold",
  cto: "realist",
  cfo: "numbers",
  coo: "realist",
  pm: "user_first",
  tax_accountant: "numbers",
  lawyer: "cautious",
  social_insurance_advisor: "cautious",
  customer: "skeptic",
  consultant: "realist",
};

export function normalizeGroupTalkMemberId(memberId: string | null | undefined): GroupTalkMemberId {
  if (typeof memberId !== "string") return "consultant";
  if (GROUP_TALK_MEMBER_IDS.includes(memberId as GroupTalkMemberId)) return memberId as GroupTalkMemberId;
  return GROUP_TALK_LEGACY_MEMBER_ID_MAP[memberId] ?? "consultant";
}

export function normalizeGroupTalkStanceId(stanceId: string | null | undefined): GroupTalkStanceId | null {
  if (typeof stanceId !== "string" || stanceId.trim().length === 0) return null;
  if (GROUP_TALK_STANCE_IDS.includes(stanceId as GroupTalkStanceId)) return stanceId as GroupTalkStanceId;
  return GROUP_TALK_LEGACY_STANCE_ID_MAP[stanceId] ?? null;
}

export function normalizeGroupTalkRecommendationTemplateId(templateId: string | null | undefined): GroupTalkTemplateId {
  if (typeof templateId !== "string" || templateId.trim().length === 0) return "management";

  const mappedTemplateId = GROUP_TALK_RECOMMENDATION_TEMPLATE_ID_MAP[templateId] ?? templateId;
  if (mappedTemplateId === "management" || mappedTemplateId === "project" || mappedTemplateId === "professional") {
    return mappedTemplateId;
  }

  return "management";
}

export function findGroupTalkMember(memberId: GroupTalkMemberId) {
  const normalizedMemberId = normalizeGroupTalkMemberId(memberId);
  return groupTalkMemberOptions.find((option) => option.id === normalizedMemberId) ?? groupTalkMemberOptions[0];
}

export function getGroupTalkDisplayMode(mode: GroupTalkDraftMode | GroupTalkDisplayMode | string | null | undefined): GroupTalkDisplayMode {
  return mode === "custom" || mode === "virtual" ? "virtual" : "named";
}

export function getGroupTalkDisplayLabel(
  memberId: GroupTalkMemberId,
  facilitatorName: string,
  displayMode: GroupTalkDraftMode | GroupTalkDisplayMode | null | undefined = "named"
) {
  const normalizedMemberId = normalizeGroupTalkMemberId(memberId);
  if (normalizedMemberId === "mei") return facilitatorName;
  const display = GROUP_TALK_MEMBER_DISPLAY[normalizedMemberId];
  return getGroupTalkDisplayMode(displayMode) === "virtual" ? display.virtualLabel : display.namedLabel;
}

function readParticipantDisplayName(participant: Pick<GroupTalkParticipant, "displayName">) {
  const displayName = typeof participant.displayName === "string" ? participant.displayName.trim() : "";
  return displayName.length > 0 ? displayName : "";
}

export function buildGroupTalkPersonaKey(memberId: GroupTalkMemberId, displayName?: string | null) {
  const normalizedMemberId = normalizeGroupTalkMemberId(memberId);
  const trimmedDisplayName = typeof displayName === "string" ? displayName.trim() : "";
  return trimmedDisplayName ? `${normalizedMemberId}:${trimmedDisplayName}` : normalizedMemberId;
}

export function getGroupTalkParticipantDisplayName(
  participant: Pick<GroupTalkParticipant, "memberId" | "stanceId" | "displayName">,
  facilitatorName: string,
  displayMode: GroupTalkDraftMode | GroupTalkDisplayMode | null | undefined = "named"
) {
  const displayName = readParticipantDisplayName(participant);
  if (displayName) return displayName;
  return buildPreviewLabel(participant.memberId, participant.stanceId, facilitatorName, displayMode);
}

export function getGroupTalkDisplayRole(memberId: GroupTalkMemberId) {
  return GROUP_TALK_MEMBER_DISPLAY[normalizeGroupTalkMemberId(memberId)].roleLabel;
}

export function getGroupTalkAvailableStances(
  memberId: GroupTalkMemberId,
  displayMode: GroupTalkDraftMode | GroupTalkDisplayMode | null | undefined = "named",
  override?: GroupTalkRoleStanceOption[]
) {
  const normalizedMemberId = normalizeGroupTalkMemberId(memberId);
  if (normalizedMemberId === "mei") return [];
  if (override && override.length > 0) return override;
  if (getGroupTalkDisplayMode(displayMode) === "named") {
    return groupTalkRoleStanceOptions[normalizedMemberId] ?? [];
  }
  return groupTalkStanceOptions.map((stance) => ({
    id: stance.id,
    label: stance.label,
    description: "",
  }));
}

export function findGroupTalkStanceLabel(
  stanceId: GroupTalkStanceId | null,
  memberId?: GroupTalkMemberId | null,
  displayMode: GroupTalkDraftMode | GroupTalkDisplayMode | null | undefined = "named",
  override?: GroupTalkRoleStanceOption[]
) {
  const normalizedStanceId = normalizeGroupTalkStanceId(stanceId);
  if (!normalizedStanceId) return null;
  if (memberId) {
    const roleLabel = getGroupTalkAvailableStances(memberId, displayMode, override).find(
      (option) => option.id === normalizedStanceId
    )?.label;
    if (roleLabel) return roleLabel;
  }
  return groupTalkStanceOptions.find((option) => option.id === normalizedStanceId)?.label ?? null;
}

export function findGroupTalkBasicStanceLabel(stanceId: GroupTalkStanceId | null) {
  const normalizedStanceId = normalizeGroupTalkStanceId(stanceId);
  if (!normalizedStanceId) return null;
  return groupTalkStanceOptions.find((option) => option.id === normalizedStanceId)?.label ?? null;
}

export function createParticipant(
  memberId: GroupTalkMemberId,
  stanceId?: GroupTalkStanceId | null,
  options?: {
    fixed?: boolean;
    availableStances?: GroupTalkRoleStanceOption[];
    displayRole?: string;
    displayName?: string;
    personaKey?: string;
    avatarKey?: string;
    imageSrc?: string;
    imageVariant?: 1 | 2;
    reason?: string;
  }
): GroupTalkParticipant {
  const normalizedMemberId = normalizeGroupTalkMemberId(memberId);
  const normalizedStanceId =
    typeof stanceId === "undefined" ? defaultGroupTalkStances[normalizedMemberId] : normalizeGroupTalkStanceId(stanceId);
  const member = findGroupTalkMember(normalizedMemberId);
  return {
    participantId: `participant_${normalizedMemberId}_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
    memberId: normalizedMemberId,
    stanceId: normalizedMemberId === "mei" ? null : normalizedStanceId,
    fixed: typeof options?.fixed === "boolean" ? options.fixed : Boolean(member.fixed),
    ...(options?.availableStances ? { availableStances: options.availableStances } : {}),
    ...(options?.displayRole ? { displayRole: options.displayRole } : {}),
    ...(options?.displayName && options.displayName.trim() ? { displayName: options.displayName.trim() } : {}),
    ...(options?.personaKey && options.personaKey.trim() ? { personaKey: options.personaKey.trim() } : {}),
    ...(options?.avatarKey && options.avatarKey.trim() ? { avatarKey: options.avatarKey.trim() } : {}),
    ...(options?.imageSrc && options.imageSrc.trim() ? { imageSrc: options.imageSrc.trim() } : {}),
    ...(options?.imageVariant ? { imageVariant: options.imageVariant } : {}),
    ...(options?.reason && options.reason.trim() ? { reason: options.reason.trim() } : {}),
  };
}

/**
 * 推薦 API の persona_key（例: cmo_main / coo_sub）から画像バリアントを求める。
 * _sub 系は 2、それ以外は 1。
 */
export function getGroupTalkImageVariantFromPersonaKey(personaKey: string | null | undefined): 1 | 2 {
  if (typeof personaKey !== "string") return 1;
  return /_sub\b/.test(personaKey.trim().toLowerCase()) ? 2 : 1;
}

export function buildParticipantsFromTemplate(templateId: GroupTalkTemplateId): GroupTalkParticipant[] {
  const template = groupTalkTemplates.find((item) => item.id === templateId) ?? groupTalkTemplates[0];
  return template.members.map((member) => createParticipant(member.memberId, member.stanceId));
}

export function isDuplicateParticipant(
  participants: GroupTalkParticipant[],
  memberId: GroupTalkMemberId,
  _stanceId?: GroupTalkStanceId | null,
  ignoreParticipantId?: string
) {
  const normalizedMemberId = normalizeGroupTalkMemberId(memberId);
  return participants.some(
    (participant) =>
      participant.participantId !== ignoreParticipantId &&
      normalizeGroupTalkMemberId(participant.memberId) === normalizedMemberId
  );
}

export function buildPreviewLabel(
  memberId: GroupTalkMemberId,
  _stanceId: GroupTalkStanceId | null,
  facilitatorName: string,
  displayMode: GroupTalkDraftMode | GroupTalkDisplayMode | null | undefined = "named"
) {
  const normalizedMemberId = normalizeGroupTalkMemberId(memberId);
  if (normalizedMemberId === "mei") return facilitatorName;
  return getGroupTalkDisplayLabel(normalizedMemberId, facilitatorName, displayMode);
}

export function getGroupTalkImageSrc(memberId: GroupTalkMemberId, appearanceIndex = 1) {
  const normalizedMemberId = normalizeGroupTalkMemberId(memberId);
  if (normalizedMemberId === "mei") {
    return `${GROUP_TALK_IMAGE_BASE_PATH}/mei.png`;
  }

  const normalizedIndex = appearanceIndex % 2 === 0 ? 2 : 1;
  const imageName = GROUP_TALK_IMAGE_NAME_BY_MEMBER_ID[normalizedMemberId] ?? normalizedMemberId;
  return `${GROUP_TALK_IMAGE_BASE_PATH}/${imageName}-${normalizedIndex}.png`;
}

export function getGroupTalkAvatarKey(memberId: GroupTalkMemberId, appearanceIndex = 1) {
  const normalizedMemberId = normalizeGroupTalkMemberId(memberId);
  if (normalizedMemberId === "mei") return "mei";
  const normalizedIndex = appearanceIndex % 2 === 0 ? 2 : 1;
  const imageName = GROUP_TALK_IMAGE_NAME_BY_MEMBER_ID[normalizedMemberId] ?? normalizedMemberId;
  return `${imageName}-${normalizedIndex}`;
}

export function getGroupTalkFallbackImageSrc() {
  return GROUP_TALK_FALLBACK_IMAGE_PATH;
}

export function buildGroupTalkParticipantImageMap<
  T extends { participantId: string; memberId: GroupTalkMemberId; imageVariant?: 1 | 2; imageSrc?: string }
>(
  participants: T[],
  displayMode: GroupTalkDraftMode | GroupTalkDisplayMode | null | undefined = "named",
  facilitatorImageSrc?: string
) {
  const defaultVariant = getGroupTalkDisplayMode(displayMode) === "virtual" ? 2 : 1;

  return new Map(
    participants.map((participant) => {
      const memberId = normalizeGroupTalkMemberId(participant.memberId);
      const storedImageSrc = typeof participant.imageSrc === "string" ? participant.imageSrc.trim() : "";
      const imageSrc =
        storedImageSrc ||
        (memberId === "mei" && facilitatorImageSrc
          ? facilitatorImageSrc
          : getGroupTalkImageSrc(memberId, participant.imageVariant ?? defaultVariant));
      return [participant.participantId, imageSrc] as const;
    })
  );
}

export function buildGroupTalkPreviewMembers(
  participants: GroupTalkParticipant[],
  facilitatorName: string,
  displayMode: GroupTalkDraftMode | GroupTalkDisplayMode | null | undefined = "named",
  facilitatorImageSrc?: string
): GroupTalkPreviewMember[] {
  const imageMap = buildGroupTalkParticipantImageMap(participants, displayMode, facilitatorImageSrc);

  return participants.map((participant) => {
    const memberId = normalizeGroupTalkMemberId(participant.memberId);
    const stanceId = normalizeGroupTalkStanceId(participant.stanceId);
    return {
      participantId: participant.participantId,
      memberId,
      label: getGroupTalkParticipantDisplayName(participant, facilitatorName, displayMode),
      roleLabel: participant.displayRole ?? getGroupTalkDisplayRole(memberId),
      stanceId,
      stanceLabel: findGroupTalkStanceLabel(stanceId, memberId, displayMode, participant.availableStances),
      basicStanceLabel: findGroupTalkBasicStanceLabel(stanceId),
      fixed: participant.fixed,
      ...(participant.personaKey ? { personaKey: participant.personaKey } : {}),
      ...(participant.avatarKey ? { avatarKey: participant.avatarKey } : {}),
      imageSrc: imageMap.get(participant.participantId) ?? getGroupTalkFallbackImageSrc(),
    };
  });
}
